/**
 * Where photographs shared in from the phone land, and the one question that
 * cannot be answered for the reader.
 *
 * The share sheet says *what* to add and never *where*: the system hands over
 * files and nothing else. So this screen exists to turn a handful of pictures
 * into a litter, and then to get out of the way — it hands over to the photo
 * wizard on that litter, with the pictures already going up and the date set to
 * today, which is what somebody sharing this evening's photographs meant.
 *
 * IT IS SKIPPED WHENEVER IT CAN BE. A kennel with one litter in the house has no
 * question to answer, and the same rule that takes that kennel straight to its
 * litter on a cold start takes it straight to the wizard here — `soleActive` in
 * lib/litters.ts, shared precisely so the two cannot drift.
 *
 * IT IS NOT THE SCREEN THAT CLAIMS THE FILES. It counts them, which is a read
 * that changes nothing, and the litter page takes them (see
 * offline/sharedPhotos.ts): a hand-off can be claimed once, and the screen that
 * asks the question is not the screen that uses the answer.
 *
 * There is no auth gate here. `__root.tsx` holds every route behind the same
 * three states, so a share that arrives at a signed-out phone waits on the
 * sign-in screen with its URL intact and this renders once there is a session —
 * the files are in IndexedDB, not in the request, so nothing is lost while the
 * reader signs in.
 */
import { canWriteLitters, type Litter } from '@puppy-doctor/platform';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLitters } from '@/api/litters.ts';
import { LitterStatusBadge } from '@/components/LitterCard.tsx';
import { PHOTOS_PARAM } from '@/components/PhotoWizard.tsx';
import { buttonClasses } from '@/components/ui.tsx';
import { asDate } from '@/lib/dates.ts';
import { photographable, soleActive } from '@/lib/litters.ts';
import { countSharedPhotos, SHARE_ID_PARAM } from '@/offline/sharedPhotos.ts';
import { useTenant, useTenantId } from '@/tenant/TenantProvider.tsx';

export const Route = createFileRoute('/share')({
  /**
   * The only route in the app with a search schema of its own, because it is the
   * only one whose URL is written by something other than the app: the service
   * worker builds it (see `receiveShare` in src/sw.ts), and what it names has to
   * be read back as a string or not at all.
   */
  validateSearch: (search: Record<string, unknown>): { shared?: string } => {
    const shared = search[SHARE_ID_PARAM];
    return typeof shared === 'string' && shared !== '' ? { shared } : {};
  },
  component: SharePage,
});

function SharePage() {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const { role } = useTenant();
  const navigate = useNavigate();
  const { shared: id } = Route.useSearch();

  const { data: litters, isPending } = useLitters(tenantId);
  const mayAdd = role !== null && canWriteLitters(role);

  /**
   * How many are waiting, or `null` while that is still being read off
   * IndexedDB.
   *
   * `null` is not `0`: one of them is a screen that has nothing to say yet and
   * the other is a screen that says the pictures did not arrive. The read is a
   * peek — see the doc comment above.
   */
  const [waiting, setWaiting] = useState<number | null>(id === undefined ? 0 : null);
  useEffect(() => {
    if (id === undefined) return;
    void countSharedPhotos(id).then(setWaiting);
  }, [id]);

  const all = litters ?? [];
  const targets = photographable(all);

  /**
   * The litter this share does not have to ask about.
   *
   * The litter in the house when there is exactly one — the home screen's own
   * rule — and otherwise the sole litter these pictures could possibly belong
   * to, for a kennel whose only litter is already closed. Everything else is a
   * question, and the list below asks it.
   */
  const sole = soleActive(all) ?? (targets.length === 1 ? targets[0]?.id ?? null : null);

  const settled = !isPending && waiting !== null;
  const jumpTo = settled && waiting > 0 && mayAdd ? sole : null;

  useEffect(() => {
    if (jumpTo === null) return;
    void navigate({
      to: '/litters/$litterId',
      params: { litterId: jumpTo },
      // The wizard's own param, plus the hand-off for it to claim. Neither is
      // in the litter route's search schema — nothing in this app declares one
      // except this route — so they are written as a plain object and read back
      // loosely there, the way every other panel param in the app is.
      search: { [PHOTOS_PARAM]: 'add', [SHARE_ID_PARAM]: id },
      // The reader never asked for this screen and must not be able to come back
      // to it: the hand-off it names is spent the moment the wizard takes it,
      // and a back tap onto a spent one is a screen saying nothing arrived.
      replace: true,
    });
  }, [jumpTo, id, navigate]);

  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-2xl font-bold tracking-[-0.02em]">{t('share.title')}</h1>

      {!settled || jumpTo !== null ? (
        // The jump happens on this same commit; painting the picker first would
        // show a question that is about to answer itself.
        <p className="text-ink-faint">{t('common.loading')}</p>
      ) : waiting === 0 ? (
        <Explain body={t('share.empty')} hint={t('share.emptyHint')} />
      ) : !mayAdd ? (
        <Explain body={t('share.readOnly')} hint={t('share.readOnlyHint')} />
      ) : targets.length === 0 ? (
        <Explain body={t('share.noLitter')} hint={t('share.noLitterHint')} />
      ) : (
        <Picker litters={targets} count={waiting} shareId={id} />
      )}
    </div>
  );
}

/**
 * Which litter, asked once and answered by tapping one.
 *
 * A BUTTON, WHICH IS THE EXCEPTION TO THE RULE IN ui.tsx rather than a lapse
 * from it. Everything that navigates in this app is a `Link`, because an address
 * is a thing worth opening in a new tab and long-pressing. What these rows carry
 * is not an address: it names a hand-off that can be claimed exactly once, so
 * the second tab to open it would find the pictures gone and the first would
 * never know. Tapping one of these is an act, and it is spelled as one.
 */
function Picker({
  litters,
  count,
  shareId,
}: {
  litters: readonly Litter[];
  count: number;
  shareId: string | undefined;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-3">
      <p className="text-ink-soft">{t('share.pick', { count })}</p>

      <ul className="flex flex-col gap-2">
        {litters.map((litter) => (
          <li key={litter.id}>
            <button
              type="button"
              onClick={() =>
                void navigate({
                  to: '/litters/$litterId',
                  params: { litterId: litter.id },
                  search: { [PHOTOS_PARAM]: 'add', [SHARE_ID_PARAM]: shareId },
                  replace: true,
                })
              }
              className="flex min-h-tap w-full cursor-pointer items-center gap-3 rounded-2xl border border-line bg-surface px-4 py-3 text-left transition-colors hover:border-line-strong hover:bg-sunken"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-semibold">{litter.name}</span>
                <span className="text-sm text-ink-faint">{asDate(litter.bornAt)}</span>
              </span>
              <LitterStatusBadge status={litter.status} />
              <ChevronRight className="size-4 shrink-0 text-ink-faint" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A dead end, said plainly, with the way back out of it. */
function Explain({ body, hint }: { body: string; hint: string }) {
  const { t } = useTranslation();

  return (
    <div className="py-8 text-center">
      <p className="font-medium text-ink-soft">{body}</p>
      <p className="mx-auto mt-1 max-w-[34ch] text-sm text-ink-faint">{hint}</p>
      <Link to="/" className={buttonClasses({ variant: 'secondary', className: 'mt-5 min-h-tap' })}>
        {t('share.toLitters')}
      </Link>
    </div>
  );
}
